import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

interface ChatApiMessage {
  role: 'user' | 'assistant';
  content: string;
}

type ChatMode = 'mock' | 'real';

interface ChatRequest {
  prompt: string;
  eoa_id: string;
  scope: string;
  context?: string;
  conversation_id?: string;
  mode: ChatMode;
  model_id?: string;
  enable_sql?: boolean;
}

interface ChatModelOption {
  id: string;
  label: string;
  supports_temperature: boolean;
}

interface ChatModelsResponse {
  available_models: ChatModelOption[];
  default_model_id: string;
}

interface ChatResponse {
  conversation_id: string;
  response: string;
  display_messages: ChatApiMessage[];
}

interface GetChatConversationResponse {
  conversation_id: string;
  display_messages: ChatApiMessage[];
  updated_at: string;
}

interface ChatConversationSummary {
  conversation_id: string;
  scope: string;
  title: string;
  updated_at: string;
}

interface ListChatConversationsResponse {
  conversations: ChatConversationSummary[];
}

const postChat = async (payload: ChatRequest, accessToken: string): Promise<ChatResponse> => {
  const response = await axios.post<ChatResponse>(`${BASE_URL}/chat`, payload, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

interface ChatStreamHandlers {
  onDelta?: (text: string) => void;
  onThinking?: (text: string) => void;
  onToolCall?: (toolName: string) => void;
  onToolResult?: (toolName: string) => void;
  signal?: AbortSignal;
}

interface ChatStreamEvent {
  type: 'delta' | 'thinking' | 'tool_call' | 'tool_result' | 'done' | 'error';
  text?: string;
  tool_name?: string;
  detail?: string;
  conversation_id?: string;
  response?: string;
  display_messages?: ChatApiMessage[];
}

// The browser EventSource API can neither POST nor send an Authorization header,
// so we read the SSE stream off a fetch ReadableStream and parse `data:` frames manually.
const postChatStream = async (
  payload: ChatRequest,
  accessToken: string,
  handlers: ChatStreamHandlers = {}
): Promise<ChatResponse> => {
  const response = await fetch(`${BASE_URL}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
    signal: handlers.signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Chat stream request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: ChatResponse | null = null;

  const handleEvent = (event: ChatStreamEvent) => {
    switch (event.type) {
      case 'delta':
        if (event.text) handlers.onDelta?.(event.text);
        break;
      case 'thinking':
        if (event.text) handlers.onThinking?.(event.text);
        break;
      case 'tool_call':
        if (event.tool_name) handlers.onToolCall?.(event.tool_name);
        break;
      case 'tool_result':
        if (event.tool_name) handlers.onToolResult?.(event.tool_name);
        break;
      case 'done':
        result = {
          conversation_id: event.conversation_id ?? '',
          response: event.response ?? '',
          display_messages: event.display_messages ?? [],
        };
        break;
      case 'error':
        throw new Error(event.detail ?? 'Chat stream error');
    }
  };

  const drainBuffer = () => {
    let sepIndex = buffer.indexOf('\n\n');
    while (sepIndex !== -1) {
      const frame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      const dataLine = frame.split('\n').find((line) => line.startsWith('data:'));
      if (dataLine) handleEvent(JSON.parse(dataLine.slice('data:'.length).trim()) as ChatStreamEvent);
      sepIndex = buffer.indexOf('\n\n');
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    drainBuffer();
  }

  if (!result) throw new Error('Chat stream ended without a completion event');
  return result;
};

const getChatConversation = async (
  eoaId: string,
  scope: string,
  accessToken: string
): Promise<GetChatConversationResponse> => {
  const response = await axios.get<GetChatConversationResponse>(`${BASE_URL}/eoa/${eoaId}/chat/conversations`, {
    params: { scope },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const listChatConversations = async (
  eoaId: string,
  accessToken: string,
  scopePrefix?: string
): Promise<ListChatConversationsResponse> => {
  const response = await axios.get<ListChatConversationsResponse>(`${BASE_URL}/eoa/${eoaId}/chat/conversations/list`, {
    params: scopePrefix ? { scope_prefix: scopePrefix } : undefined,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

const getChatModels = async (eoaId: string, accessToken: string): Promise<ChatModelsResponse> => {
  const response = await axios.get<ChatModelsResponse>(`${BASE_URL}/eoa/${eoaId}/chat/models`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

export { postChat, postChatStream, getChatConversation, listChatConversations, getChatModels };
export type {
  ChatMode,
  ChatRequest,
  ChatResponse,
  ChatApiMessage,
  ChatStreamHandlers,
  GetChatConversationResponse,
  ChatConversationSummary,
  ListChatConversationsResponse,
  ChatModelOption,
  ChatModelsResponse,
};
